/**
 * Standardized Error Handling Utility for FleetManager
 *
 * Provides consistent error handling across all components:
 * - Extracts user-friendly error messages from various error formats
 * - Logs errors consistently for debugging
 * - Integrates with toast notification system
 *
 * @module utils/errorHandler
 *
 * i18n : module hors React → `i18n.t()` est appelé directement, JAMAIS au
 * chargement du module (ça figerait la langue de démarrage). `ERROR_CONTEXTS`
 * est donc un objet à GETTERS : sa forme publique (`ERROR_CONTEXTS.load`) est
 * inchangée, mais la valeur est résolue à la lecture, dans la langue courante.
 */
import i18n from '../i18n/index.js';

// =============================================================================
// ERROR MESSAGE EXTRACTION
// =============================================================================

/**
 * Extract a user-friendly error message from various error formats
 *
 * Handles:
 * - API response errors: { error: { message: '...' } } or { error: '...' }
 * - Axios/fetch errors: error.response?.data?.error
 * - Standard Error objects: error.message
 * - String errors
 *
 * @param {Error|Object|string} error - The error to extract message from
 * @param {string} [fallback='Une erreur est survenue'] - Fallback message if extraction fails
 * @returns {string} User-friendly error message
 *
 * @example
 * try {
 *   await api.vehicle.create(data);
 * } catch (error) {
 *   const message = getErrorMessage(error);
 *   // Returns: "Le champ 'license_plate' est requis" or fallback
 * }
 */
export function getErrorMessage(error, fallback = null) {
  const fb = fallback || i18n.t('common.states.error');
  if (!error) return fb;

  // String error
  if (typeof error === 'string') {
    return error;
  }

  // API response format: { error: { message: '...' } }
  if (error.error?.message) {
    return error.error.message;
  }

  // API response format: { error: '...' }
  if (typeof error.error === 'string') {
    return error.error;
  }

  // Axios/fetch response error
  if (error.response?.data?.error?.message) {
    return error.response.data.error.message;
  }
  if (error.response?.data?.error) {
    return typeof error.response.data.error === 'string'
      ? error.response.data.error
      : fb;
  }
  if (error.response?.data?.message) {
    return error.response.data.message;
  }

  // Standard Error object
  if (error.message) {
    // Don't expose technical messages to users
    if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
      return i18n.t('common.errors.network');
    }
    if (error.message.includes('401') || error.message.includes('Unauthorized')) {
      return i18n.t('common.errors.sessionExpired');
    }
    if (error.message.includes('403') || error.message.includes('Forbidden')) {
      return i18n.t('common.errors.forbidden');
    }
    if (error.message.includes('404') || error.message.includes('Not Found')) {
      return i18n.t('common.errors.notFound');
    }
    if (error.message.includes('500') || error.message.includes('Internal Server')) {
      return i18n.t('common.errors.server');
    }
    return error.message;
  }

  return fb;
}

// =============================================================================
// ERROR CONTEXT MESSAGES
// =============================================================================

/**
 * Common error context messages (resolved through i18n at read time)
 * Use these for consistent messaging across the app
 */
const ERROR_CONTEXT_KEYS = [
  'load',
  'create',
  'update',
  'delete',
  'save',
  'upload',
  'download',
  'export',
  'import',
  'search',
  'login',
  'logout',
  'auth',
  'submit',
  'approve',
  'reject',
  'transition',
];

export const ERROR_CONTEXTS = {};
for (const name of ERROR_CONTEXT_KEYS) {
  Object.defineProperty(ERROR_CONTEXTS, name, {
    enumerable: true,
    get: () => i18n.t(`common.errorContexts.${name}`),
  });
}

// =============================================================================
// MAIN ERROR HANDLER
// =============================================================================

/**
 * Handle an error with logging and optional user notification
 *
 * @param {Error|Object|string} error - The error to handle
 * @param {string} context - Context description (e.g., 'creating vehicle', 'loading drivers')
 * @param {Object} [options] - Options
 * @param {Function} [options.onMessage] - Toast notification function: ({title, message, type}) => void
 * @param {boolean} [options.silent=false] - If true, don't show user notification
 * @param {boolean} [options.log=true] - If true, log to console
 * @param {string} [options.title='Erreur'] - Toast title
 * @returns {string} The extracted error message
 *
 * @example
 * // In a component:
 * const handleSave = async () => {
 *   try {
 *     await api.vehicle.create(formData);
 *     onMessage?.({ title: 'Succès', message: 'Véhicule créé', type: 'success' });
 *   } catch (error) {
 *     handleApiError(error, 'creating vehicle', { onMessage });
 *   }
 * };
 */
export function handleApiError(error, context, options = {}) {
  const {
    onMessage,
    silent = false,
    log = true,
    title = i18n.t('common.errors.title'),
  } = options;

  const message = getErrorMessage(error);

  // Log to console for debugging
  if (log) {
    console.error(`[${context}]`, error);
  }

  // Show user notification
  if (!silent && onMessage) {
    onMessage({
      title,
      message: `${ERROR_CONTEXTS[context] || context}: ${message}`,
      type: 'error',
    });
  }

  return message;
}

/**
 * Simplified error handler for common CRUD operations
 *
 * @param {Error|Object|string} error - The error
 * @param {'load'|'create'|'update'|'delete'|'save'} operation - The operation type
 * @param {string} entityName - Name of the entity (e.g., 'véhicule', 'conducteur')
 * @param {Function} [onMessage] - Toast notification function
 * @returns {string} The extracted error message
 *
 * @example
 * try {
 *   await api.vehicle.delete(id);
 * } catch (error) {
 *   handleCrudError(error, 'delete', 'véhicule', onMessage);
 *   // Shows: "Erreur lors de la suppression du véhicule: [message]"
 * }
 */
export function handleCrudError(error, operation, entityName, onMessage) {
  // `entityName` vient du code appelant (« véhicule », « conducteur ») : c'est un
  // mot de la langue de l'appelant, pas une clé — il est interpolé tel quel.
  const CRUD_OPERATIONS = ['load', 'create', 'update', 'delete', 'save'];
  const context = CRUD_OPERATIONS.includes(operation)
    ? i18n.t(`common.errorContexts.crud.${operation}`, { entity: entityName })
    : operation;
  return handleApiError(error, context, { onMessage });
}

// =============================================================================
// FORM VALIDATION ERRORS
// =============================================================================

/**
 * @typedef {Object} ValidationError
 * @property {string} field - Field name that failed validation
 * @property {string} message - Error message
 */

/**
 * Extract validation errors from API response
 *
 * @param {Object} error - API error response
 * @returns {ValidationError[]} Array of validation errors
 *
 * @example
 * const errors = getValidationErrors(apiError);
 * // Returns: [{ field: 'email', message: 'Email invalide' }]
 */
export function getValidationErrors(error) {
  const errors = [];

  // Format: { error: { fields: { email: 'Invalid email' } } }
  if (error?.error?.fields) {
    for (const [field, message] of Object.entries(error.error.fields)) {
      errors.push({ field, message: String(message) });
    }
    return errors;
  }

  // Format: { errors: [{ field: 'email', message: 'Invalid' }] }
  if (Array.isArray(error?.errors)) {
    return error.errors.map(e => ({
      field: e.field || e.path || 'unknown',
      message: e.message || e.msg || String(e),
    }));
  }

  return errors;
}

/**
 * Check if an error is a validation error (400 status)
 *
 * @param {Object} error - Error object
 * @returns {boolean}
 */
export function isValidationError(error) {
  return error?.response?.status === 400 ||
         error?.status === 400 ||
         error?.error?.type === 'validation';
}

// =============================================================================
// ASYNC ERROR WRAPPER
// =============================================================================

/**
 * Wrap an async function with standardized error handling
 *
 * @param {Function} asyncFn - Async function to wrap
 * @param {string} context - Error context
 * @param {Object} [options] - handleApiError options
 * @returns {Function} Wrapped function that catches and handles errors
 *
 * @example
 * const loadDrivers = withErrorHandling(
 *   async () => {
 *     const res = await api.driver.getAll();
 *     setDrivers(res.data);
 *   },
 *   'load',
 *   { onMessage }
 * );
 *
 * // Later:
 * loadDrivers(); // Errors are automatically handled
 */
export function withErrorHandling(asyncFn, context, options = {}) {
  return async (...args) => {
    try {
      return await asyncFn(...args);
    } catch (error) {
      handleApiError(error, context, options);
      return null;
    }
  };
}

// =============================================================================
// DEFAULT EXPORT
// =============================================================================

const errorHandler = {
  getErrorMessage,
  handleApiError,
  handleCrudError,
  getValidationErrors,
  isValidationError,
  withErrorHandling,
  ERROR_CONTEXTS,
};

export default errorHandler;
